import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiCall, ApiError } from '../utils/apiHelpers';
import type { TestDecodeResponse } from '../types';

export function TestDecodePage() {
  const [searchParams] = useSearchParams();
  const p = searchParams.get('p');
  const [result, setResult] = useState<TestDecodeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!p) {
      setLoading(false);
      setError('Missing payload parameter.');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await apiCall<TestDecodeResponse>(`/api/test-decode?p=${encodeURIComponent(p)}`);
        if (!cancelled) setResult(r);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.message : (e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [p]);

  return (
    <div className="signin-page">
      <div
        className="signin-page__card"
        style={{ maxWidth: 560, textAlign: 'left' }}
      >
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Decoded QR</h1>
        {loading && <p>Loading…</p>}
        {error && (
          <div style={{ padding: 12, background: '#fee', border: '1px solid #fcc', borderRadius: 6 }}>
            {error}
          </div>
        )}
        {result && !result.valid && (
          <div
            style={{
              padding: 12,
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid var(--danger-color)',
              borderRadius: 6,
            }}
          >
            <strong>Invalid QR code.</strong>
            {result.reason && <p style={{ margin: '4px 0 0' }}>{result.reason}</p>}
          </div>
        )}
        {result && result.valid && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              {result.isTest && (
                <span
                  style={{
                    background: 'var(--warning-color, #d97706)',
                    color: '#fff',
                    padding: '2px 8px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Test code · 24h
                </span>
              )}
              {result.schemaName && (
                <h2 style={{ margin: 0, fontSize: '1.1rem' }}>{result.schemaName}</h2>
              )}
            </div>
            {result.expiresAt && (
              <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                Expires {new Date(result.expiresAt).toLocaleString()}
              </p>
            )}
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 14,
                marginTop: 12,
                border: '1px solid var(--border-color)',
                borderRadius: 6,
                overflow: 'hidden',
              }}
            >
              <tbody>
                {Object.entries(result.fields ?? {}).map(([k, v]) => (
                  <tr key={k} style={{ borderTop: '1px solid var(--border-color)' }}>
                    <th
                      style={{
                        padding: 12,
                        textAlign: 'left',
                        background: 'var(--bg-color, #fafafa)',
                        fontSize: 13,
                        color: 'var(--text-muted)',
                        width: '40%',
                      }}
                    >
                      {k}
                    </th>
                    <td style={{ padding: 12 }}>{formatVal(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function formatVal(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}
